import type { EPCR, Incident, FireReport } from '@/types';

/**
 * demo-data — plausible placeholder values for each ePCR section.
 *
 * Used by the "Fill Demo Data" buttons that appear on each tab when
 * demo mode is on. Every returned value is obviously-fake-but-
 * clinically-plausible — enough to get through the workflow without
 * being mistaken for a real patient.
 *
 * Names use agency-safe "Demo …" prefixes so exported reports are
 * trivially identified as training data.
 */

/**
 * Demo rescue units — R1 through R9. Demo mode picks one at
 * random so a click-through demo shows a realistic unit roster.
 */
export const DEMO_RESCUE_UNITS = [
  'R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7', 'R8', 'R9',
] as const;

export function demoRescueUnit(): string {
  return DEMO_RESCUE_UNITS[Math.floor(Math.random() * DEMO_RESCUE_UNITS.length)];
}

/**
 * Plausible demo dispatch locations — mixed residential /
 * commercial / public spaces so demos show variety. All geocoded close
 * enough for mapping widgets to render a reasonable pin.
 */
const DEMO_LOCATIONS = [
  { address: '375 Public Safety Way', city: 'Providence', zip: '02903', lat: 41.8240, lng: -71.4128 },
  { address: '593 Eddy Street',       city: 'Providence', zip: '02903', lat: 41.8105, lng: -71.4028 },
  { address: '222 Richmond Street',   city: 'Providence', zip: '02903', lat: 41.8194, lng: -71.4085 },
  { address: '101 Dudley Street',     city: 'Providence', zip: '02905', lat: 41.8063, lng: -71.4126 },
  { address: '170 Westminster Street',city: 'Providence', zip: '02903', lat: 41.8227, lng: -71.4140 },
  { address: '60 Eddy Street',        city: 'Providence', zip: '02903', lat: 41.8242, lng: -71.4094 },
];

const DEMO_NATURE_CODES = [
  'Chest Pain / ACS',
  'Difficulty Breathing',
  'Fall with Injury',
  'Altered Mental Status',
  'Motor Vehicle Crash',
  'Seizure',
];

export function demoIncidentFields(): Partial<Incident> & { unit_id?: string } {
  const now = new Date();
  const callRecv = new Date(now.getTime() - 22 * 60_000); // 22 min ago — PSAP receives
  const callEnt  = new Date(callRecv.getTime() + 30_000); // +30 sec — CAD entry
  const dispatch = new Date(callEnt.getTime()  + 90_000); // +1.5 min — unit dispatched
  const enRoute  = new Date(dispatch.getTime() + 60_000);
  const onScene  = new Date(dispatch.getTime() + 6 * 60_000);
  const atPt     = new Date(dispatch.getTime() + 7 * 60_000);
  const transport = new Date(dispatch.getTime() + 12 * 60_000);
  const atHosp   = new Date(dispatch.getTime() + 22 * 60_000);
  const tocOther = new Date(atHosp.getTime() + 8 * 60_000);
  const avail    = new Date(atHosp.getTime() + 14 * 60_000);
  const inQtrs   = new Date(avail.getTime() + 9 * 60_000);

  const loc = DEMO_LOCATIONS[Math.floor(Math.random() * DEMO_LOCATIONS.length)];
  const nature = DEMO_NATURE_CODES[Math.floor(Math.random() * DEMO_NATURE_CODES.length)];

  return {
    cad_number: 'DEMO-' + Math.floor(Math.random() * 9000 + 1000),
    run_number: 'D' + Math.floor(Math.random() * 9000 + 1000),
    address: loc.address,
    city: loc.city,
    state: 'RI',
    zip: loc.zip,
    gps_lat: loc.lat,
    gps_lng: loc.lng,
    cad_nature_code: nature,
    cad_priority: 'Priority 1',
    // ALL eleven NEMSIS run-time stamps — matches the TimestampRail's
    // FIELDS array so a one-click demo fills every chip on the rail.
    call_received_time: callRecv.toISOString(),
    call_entered_time:  callEnt.toISOString(),
    dispatch_time: dispatch.toISOString(),
    en_route_time: enRoute.toISOString(),
    on_scene_time: onScene.toISOString(),
    at_patient_time: atPt.toISOString(),
    transport_time: transport.toISOString(),
    at_hospital_time: atHosp.toISOString(),
    transfer_care_other_service_time: tocOther.toISOString(),
    available_time: avail.toISOString(),
    back_in_quarters_time: inQtrs.toISOString(),
    incident_types: ['ems'] as Incident['incident_types'],
    unit_id: demoRescueUnit(),
  };
}

export function demoPatientFields(): Partial<EPCR> {
  // Age ~62, adult
  const dob = new Date();
  dob.setFullYear(dob.getFullYear() - 62);
  dob.setMonth(4);
  dob.setDate(15);
  return {
    patient_first_name: 'Demo',
    patient_last_name: 'Patient-' + Math.floor(Math.random() * 900 + 100),
    patient_dob: dob.toISOString().slice(0, 10),
    patient_gender: 'Male',
    patient_weight_kg: 82,
    medical_history: [
      'Hypertension (HTN)',
      'Coronary Artery Disease (CAD)',
      'Diabetes Mellitus — Type 2',
    ],
    current_medications: [
      { name: 'Lisinopril', dosage: '20 mg daily', generic_name: null },
      { name: 'Metformin', dosage: '500 mg BID', generic_name: null },
      { name: 'Aspirin', dosage: '81 mg daily', generic_name: null },
    ],
    allergies: [
      { agent: 'Penicillin', drug_class: null, reaction_type: 'Rash' },
    ],
  } as unknown as Partial<EPCR>;
}

export function demoChiefComplaintFields(): Partial<EPCR> {
  return {
    dispatch_reason: 'Chest Pain',
    chief_complaint: 'Chest Pain',
    primary_impression: 'Acute Coronary Syndrome (ACS)',
    secondary_impressions: ['Hypertension (Asymptomatic)'],
    signs: ['Diaphoretic', 'Pale', 'Tachycardia'],
    symptoms: ['Chest Pain', 'Shortness of Breath', 'Nausea'],
    scene_safety_flags: [],
  };
}

export interface DemoVitalsSet {
  bp_systolic: string;
  bp_diastolic: string;
  heart_rate: string;
  respiratory_rate: string;
  spo2: string;
  etco2: string;
  temperature: string;
  blood_glucose: string;
  gcs_eye: string;
  gcs_verbal: string;
  gcs_motor: string;
  pain_scale: string;
  heart_rhythm: string;
}

export function demoVitalsSet(): DemoVitalsSet {
  return {
    bp_systolic: '148',
    bp_diastolic: '92',
    heart_rate: '104',
    respiratory_rate: '22',
    spo2: '96',
    etco2: '38',
    temperature: '98.4',
    blood_glucose: '132',
    gcs_eye: '4',
    gcs_verbal: '5',
    gcs_motor: '6',
    pain_scale: '8',
    heart_rhythm: 'Sinus Tachycardia',
  };
}

export function demoDispositionFields(): Partial<EPCR> {
  return {
    transport_decision: 'transport_this_unit',
    destination_hospital: 'Rhode Island Hospital',
    hospital_choice_reason: 'Cardiac Center',
    // Crew + patient evaluation/care fields — required for NERIS,
    // typed against the EPCR schema's literal unions so we don't
    // need a cast.
    crew_disposition: 'Initiated and Continued Primary Care',
    patient_evaluation_care: 'Patient Evaluated and Care Provided',
  } as Partial<EPCR>;
}

/**
 * Assessment tab demo — primary impression + body-system assessments.
 *
 * Section status uses epcr.assessments.length > 0 to flip from
 * "pending" → "complete," so the demo MUST populate at least one
 * BodySystemAssessment. Cardiovascular + Respiratory match the
 * chest-pain scenario.
 */
export function demoAssessmentFields(): Partial<EPCR> {
  return {
    primary_impression: 'Acute Coronary Syndrome (ACS)',
    secondary_impressions: ['Hypertension (Asymptomatic)'],
    signs: ['Diaphoretic', 'Pale', 'Tachycardia'],
    symptoms: ['Chest Pain', 'Shortness of Breath', 'Nausea'],
    assessments: [
      {
        system: 'neurological',
        findings: {
          loc: 'Alert',
          gcs_eye: '4',
          gcs_verbal: '5',
          gcs_motor: '6',
          pupils: 'PERRL',
          speech: 'Normal',
          headache: false,
          dizziness: false,
          syncope: false,
          seizure_activity: false,
        },
      },
      {
        system: 'respiratory',
        findings: {
          rate: 'Tachypneic',
          rhythm: 'Regular',
          effort: 'Labored',
          breath_sounds_left: 'Clear',
          breath_sounds_right: 'Clear',
          accessory_muscles: false,
        },
      },
      {
        system: 'cardiovascular',
        findings: {
          rate: 'Tachycardic',
          rhythm: 'Regular',
          twelve_lead: 'No STEMI; anterior T-wave flattening',
          skin_color: 'Pale',
          skin_temp: 'Cool',
          skin_moisture: 'Diaphoretic',
          peripheral_pulses: 'Present/Equal',
          edema: 'None',
        },
      },
      {
        system: 'skin',
        findings: {
          color: 'Pale',
          temperature: 'Cool',
          moisture: 'Diaphoretic',
          rash: false,
          wound_description: 'No wounds',
        },
      },
    ],
  };
}

/**
 * Transfer-of-care demo — receiving provider info that satisfies the
 * NEMSIS handoff slot AND drives the narrative engine's handoff
 * paragraph.
 */
export function demoTransferOfCareFields(): Partial<EPCR> {
  return {
    // Free-text receiving fields are tolerated by the EPCR schema as
    // unknown extensions; cast through unknown so we don't leak the
    // schema mismatch warning.
    ...({
      receiving_provider_name: 'Dr. Sarah Chen',
      receiving_provider_credential: 'MD',
      transfer_of_care_time: new Date().toISOString(),
    } as unknown as Partial<EPCR>),
  };
}

/**
 * Fire-report demo — comprehensive populate so the FireReportPage
 * Sections A through M all show plausible values, not just the four
 * NERIS-required slots. The chief's "the NERIS form looks empty"
 * complaint is fixed by writing values into every visible section.
 *
 * Scenario: residential kitchen fire, single-family dwelling,
 * unintentional ignition, contained quickly, no casualties.
 */
export function demoFireReportFields(): Partial<FireReport> {
  const now = new Date();
  const alarmTime = new Date(now.getTime() - 22 * 60_000);
  const arrival  = new Date(alarmTime.getTime() + 6 * 60_000);
  const controlled = new Date(alarmTime.getTime() + 14 * 60_000);
  const lastClear  = new Date(alarmTime.getTime() + 38 * 60_000);

  return {
    // A — Incident header
    neris_incident_types: ['111 — Building Fire'],
    alarm_level: '1st Alarm',

    // B — Times (echoes the parent incident, but the report owns its
    //          own copies for offline-friendly reads)
    alarm_date_time: alarmTime.toISOString(),
    arrival_time: arrival.toISOString(),
    fire_controlled_time: controlled.toISOString(),
    last_unit_cleared_time: lastClear.toISOString(),

    // C — Incident type & actions
    actions_taken: ['Fire control or extinguishment, other', 'Salvage & overhaul', 'Investigate'],

    // D — Property & structure
    occupancy_type: '419 — 1 or 2 Family Dwelling',
    construction_type: 'Type V - Wood Frame',
    year_built: 1958,
    stories: 2,
    total_square_feet: 1850,
    property_use_detail: 'Single-family residential',
    floor_of_origin: '1',
    area_of_origin: '24 — Cooking Area / Kitchen',

    // E — Cause of ignition & human factors
    fire_origin_room: 'Kitchen',
    fire_origin_floor: '1',
    ignition_factor: 'Unattended cooking equipment',
    cause: 'unintentional',
    cause_category: 'Cooking',
    cause_narrative: 'Unattended pan of oil on stovetop ignited; occupant returned to find flames at range top.',
    human_factor: 'Cooking unattended',
    human_factors: ['Asleep', 'Unattended person'],
    item_first_ignited: 'Cooking materials, including food',
    material_first_ignited: 'Vegetable oil',
    heat_source: 'Heat from radiated, conducted from operating equipment',
    fire_spread: 'Confined to object of origin',

    // F — Tactical benchmarks (timestamped at IC milestones)
    tactical_benchmarks: {
      'On Scene Size-Up': new Date(alarmTime.getTime() + 6 * 60_000).toISOString(),
      'Water on Fire': new Date(alarmTime.getTime() + 9 * 60_000).toISOString(),
      'Fire Knocked Down': new Date(alarmTime.getTime() + 12 * 60_000).toISOString(),
      'Fire Under Control': controlled.toISOString(),
      'Primary Search Complete': new Date(alarmTime.getTime() + 11 * 60_000).toISOString(),
      'Secondary Search Complete': new Date(alarmTime.getTime() + 18 * 60_000).toISOString(),
    },
    command_officer_name: 'BC Murphy (BC-2)',
    ic_transferred_to: 'BC Murphy (BC-2)',

    // G — Water supply & lines
    water_supply_type: 'Municipal hydrant',
    water_used_gallons: 250,
    hydrant_location: 'Eddy St & Public Safety Way',
    lines_deployed: [
      { size: '1¾ in', count: 1 },
      { size: '2½ in', count: 1 },
    ],

    // H — Detection & suppression systems
    detector_type: 'Smoke detector — battery only',
    detector_performance: 'Operated and effective',
    detector_power: 'Battery-only',
    suppression_method: 'Direct attack',
    suppression_system_type: 'None present',
    suppression_system_performance: 'N/A',
    agents_used: ['Water'],

    // Sprinkler (none in this scenario — residential)
    sprinkler_present: false,
    sprinkler_activated: false,
    sprinkler_type: null,
    sprinklers_operated_count: null,

    // I — Losses
    property_loss_usd: 4500,
    contents_loss_usd: 1200,
    property_value_usd: 320000,
    contents_value_usd: 60000,
    flame_damage_extent: 'Confined to object of origin',
    smoke_damage_extent: 'Confined to room of origin',

    // J — Mutual aid
    mutual_aid_type: 'None',
    mutual_aid_agencies: [],
    ...({ mutual_aid: false } as unknown as Partial<FireReport>),

    // K — Hazmat (none in this scenario)
    hazmat_class: null,
    hazmat_un_number: null,
    hazmat_released: false,
    hazmat_release_qty_desc: null,

    // M — Narrative
    narrative: [
      'DEMO — not a real incident.',
      '',
      'Engine 1 and Rescue dispatched to a single-family dwelling for a reported kitchen fire. Upon arrival, light smoke showing from the second-floor kitchen window. Occupant met crew at the front porch, reported having left a pan of oil on an active burner.',
      '',
      'Engine 1 stretched a 1¾" line through the front door to the kitchen on the first floor (rear), found a small fire at the range top contained to the pan and adjacent backsplash. Knocked down with approximately 50 gallons. Salvage covers deployed. Primary and secondary searches negative.',
      '',
      'Cause: unintentional, unattended cooking. No injuries to occupants or firefighters. Smoke alarm operated and woke the occupant. Property turned over to occupant with cooking-safety briefing. Cleared scene at last-unit-cleared time.',
    ].join('\n'),

    // Pilot agency admin
    shift_platoon: 'B',
    box_number: '2-1-7',
    run_card: 'Box 217',
  };
}

/**
 * Demo procedure rows — for ProceduresTab demo fill. Returns a few
 * NEMSIS-coded procedures consistent with the chest-pain scenario:
 * IV access + 12-lead ECG. Caller writes each as a Procedure record.
 */
export function demoProcedureRows(epcrId: string, performedBy: string): Array<{
  id: string;
  epcr_id: string;
  timestamp: string;
  procedure_type: string;
  body_site: string | null;
  performed_by: string;
  attempt_number: number;
  outcome: 'successful' | 'unsuccessful' | 'not_attempted';
  complications: string | null;
  protocol_id: string | null;
  protocol_step_order: number | null;
  is_deviation: boolean;
  deviation_reason: string | null;
  created_at: string;
}> {
  const now = new Date();
  const t1 = new Date(now.getTime() - 8 * 60_000).toISOString();
  const t2 = new Date(now.getTime() - 6 * 60_000).toISOString();
  const t3 = new Date(now.getTime() - 4 * 60_000).toISOString();
  return [
    {
      id: crypto.randomUUID(),
      epcr_id: epcrId,
      timestamp: t1,
      procedure_type: 'IV Access',
      body_site: 'Left AC',
      performed_by: performedBy,
      attempt_number: 1,
      outcome: 'successful',
      complications: null,
      protocol_id: 'ri_4_04_acs',
      protocol_step_order: 1,
      is_deviation: false,
      deviation_reason: null,
      created_at: t1,
    },
    {
      id: crypto.randomUUID(),
      epcr_id: epcrId,
      timestamp: t2,
      procedure_type: '12-Lead Acquisition',
      body_site: null,
      performed_by: performedBy,
      attempt_number: 1,
      outcome: 'successful',
      complications: null,
      protocol_id: 'ri_4_04_acs',
      protocol_step_order: 2,
      is_deviation: false,
      deviation_reason: null,
      created_at: t2,
    },
    {
      id: crypto.randomUUID(),
      epcr_id: epcrId,
      timestamp: t3,
      procedure_type: 'Cardiac Monitoring',
      body_site: null,
      performed_by: performedBy,
      attempt_number: 1,
      outcome: 'successful',
      complications: null,
      protocol_id: 'ri_4_04_acs',
      protocol_step_order: 3,
      is_deviation: false,
      deviation_reason: null,
      created_at: t3,
    },
  ];
}

/**
 * Demo medication rows — Aspirin + NTG, the two most common in the
 * chest-pain scenario. Caller writes each as a MedicationGiven record.
 */
export function demoMedicationRows(epcrId: string, performedBy: string): Array<{
  id: string;
  epcr_id: string;
  drug_name: string;
  dose: string;
  unit: string;
  route: string;
  timestamp: string;
  performed_by: string;
  patient_response: string | null;
  controlled_substance: boolean;
  waste_documented: boolean;
  waste_witness: string | null;
  protocol_id: string | null;
  protocol_step_order: number | null;
  is_deviation: boolean;
  deviation_reason: string | null;
  created_at: string;
}> {
  const now = new Date();
  const t1 = new Date(now.getTime() - 7 * 60_000).toISOString();
  const t2 = new Date(now.getTime() - 5 * 60_000).toISOString();
  return [
    {
      id: crypto.randomUUID(),
      epcr_id: epcrId,
      drug_name: 'Aspirin',
      dose: '324',
      unit: 'mg',
      route: 'PO',
      timestamp: t1,
      performed_by: performedBy,
      patient_response: 'Chewed and swallowed without difficulty.',
      controlled_substance: false,
      waste_documented: false,
      waste_witness: null,
      protocol_id: 'ri_4_04_acs',
      protocol_step_order: 0,
      is_deviation: false,
      deviation_reason: null,
      created_at: t1,
    },
    {
      id: crypto.randomUUID(),
      epcr_id: epcrId,
      drug_name: 'Nitroglycerin',
      dose: '0.4',
      unit: 'mg',
      route: 'SL',
      timestamp: t2,
      performed_by: performedBy,
      patient_response: 'Pain decreased from 8/10 to 5/10.',
      controlled_substance: false,
      waste_documented: false,
      waste_witness: null,
      protocol_id: 'ri_4_04_acs',
      protocol_step_order: 4,
      is_deviation: false,
      deviation_reason: null,
      created_at: t2,
    },
  ];
}

/**
 * Master combiner — applies every per-section helper in one shot for
 * a "fill the entire ePCR" affordance on the EPCR header. Returns a
 * single Partial<EPCR> the caller can merge via onUpdate(). Incident
 * + Fire Report fills are intentionally NOT included here because
 * those are owned by sibling records, not the EPCR; demo their fills
 * via demoIncidentFields() / demoFireReportFields() at the call site.
 */
export function demoFullEpcrFields(): Partial<EPCR> {
  return {
    ...demoPatientFields(),
    ...demoChiefComplaintFields(),
    ...demoAssessmentFields(),
    ...demoDispositionFields(),
    ...demoTransferOfCareFields(),
  };
}

export function demoNarrative(): string {
  return [
    'DEMO RUN — not a real patient.',
    '',
    'Rescue dispatched emergency to a private residence for a report of chest pain. Upon arrival, crew found a 62-year-old male seated in a chair, alert and oriented x 4, pale, diaphoretic, complaining of substernal chest pressure radiating to left arm, onset 20 minutes prior, 8/10 intensity, associated shortness of breath and nausea.',
    '',
    'History of CAD, HTN, and DM-II. Medications include lisinopril, metformin, and aspirin daily. NKDA except penicillin (rash). Last oral intake approximately 4 hours ago.',
    '',
    'Initial vitals: BP 148/92, HR 104 sinus tach, RR 22, SpO2 96% RA, EtCO2 38, GCS 15, pain 8/10.',
    '',
    'Treatment: oxygen via nasal cannula 4 LPM, full ALS monitor applied, 12-lead ECG acquired (no STEMI pattern — anterior T-wave flattening), IV established left AC 18 g NS KVO, aspirin 324 mg PO chewed, nitroglycerin 0.4 mg SL × 1 with improvement to 5/10.',
    '',
    'Transport priority to Rhode Island Hospital (PCI-capable) with continuous monitoring. Repeat vitals en route stable. Patient delivered to RIH ED, report given to receiving nurse, care transferred without incident.',
  ].join('\n');
}
